import React, { useState, useEffect } from "react";
import { Loader2, AlertCircle } from "lucide-react";

/**
 * Interface VideoPlayerProps {
 *     src: string;
 *     className?: string;
 * }
 */

const VideoPlayer = ({ src, className }) => {
  const [videoSrc, setVideoSrc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    let mounted = true;
    const loadVideoFully = async () => {
      try {
        setLoading(true);
        setError(null);
        setProgress(0);

        // Use fetch to get the blob
        const response = await fetch(src);

        if (!response.ok) {
          throw new Error(
            `Failed to load video: ${response.status} ${response.statusText}`,
          );
        }

        // Clone response to read headers for progress if needed, but standard fetch doesn't support progress on body read easily without specific reader logic.
        // For simplicity as requested, just bubble wait until blob is ready.
        // If we want progress we need to use a reader steps, but user code snippet was simple.

        const blob = await response.blob();

        if (mounted) {
          const localUrl = URL.createObjectURL(blob);
          setVideoSrc(localUrl);
          setLoading(false);
        }
      } catch (err) {
        console.error("Error loading video fully", err);
        if (mounted) {
          setError("Failed to load video");
          setLoading(false);
        }
      }
    };

    if (src) {
      loadVideoFully();
    } else {
      setLoading(false);
    }

    return () => {
      mounted = false;
      if (videoSrc) {
        URL.revokeObjectURL(videoSrc);
      }
    };
  }, [src]);

  if (loading) {
    return (
      <div
        className={`flex flex-col items-center justify-center bg-gray-100 text-gray-500 rounded-lg ${className}`}
      >
        <Loader2 className="w-8 h-8 animate-spin mb-2" />
        <span className="text-sm font-medium">Loading video...</span>
        <span className="text-xs text-gray-400 mt-1">
          Please wait while the video downloads fully
        </span>
      </div>
    );
  }

  if (error) {
    return (
      <div
        className={`flex flex-col items-center justify-center bg-red-50 text-red-500 rounded-lg ${className}`}
      >
        <AlertCircle className="w-8 h-8 mb-2" />
        <span className="text-sm font-medium">Unable to load video</span>
        <button
          onClick={() => window.open(src, "_blank")}
          className="mt-2 text-xs underline hover:text-red-700"
        >
          Try opening directly
        </button>
      </div>
    );
  }

  return (
    <video className={className} controls playsInline src={videoSrc || ""} />
  );
};

export default VideoPlayer;
